<template>
  <div class="type-categories-view">
    <BreadcrumbBar 
      :parentName="station?.name || 'Station'"
      currentName="Categories"
      :backRoute="station ? `/station/${station.slug}` : '/'"
    />

    <div class="content" v-if="station">
      <!-- Toolbar -->
      <div class="content-toolbar">
        <div class="toolbar-left"></div>
        <div class="toolbar-right">
          <button class="btn-secondary" @click="showCopyModal = true" :disabled="otherStations.length === 0">
            <AppIcon name="copy" />Copy from Station
          </button>
          <button class="btn-primary" @click="saveCategories" :disabled="saving || !hasChanges">
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </div>

      <div class="info-banner">
        <AppIcon name="info-circle" />
        <span>Map your automation system's type codes to display labels and colors. Codes are matched case-insensitively.</span>
      </div>

      <div class="default-color-row">
        <label>Default color for unmatched codes:</label>
        <div class="color-input-group">
          <input type="color" v-model="defaultTypeColor" class="color-picker">
          <input type="text" v-model="defaultTypeColor" class="color-text" maxlength="7">
        </div>
      </div>

      <div class="categories-list">
        <div v-for="(cat, index) in categories" :key="cat.id" class="category-row">
          <div class="category-color">
            <input type="color" v-model="cat.color" class="color-picker">
          </div>
          <div class="category-fields">
            <div class="field-group">
              <label>Label</label>
              <input type="text" v-model="cat.label" placeholder="e.g., MUSIC" class="input-label">
            </div>
            <div class="field-group field-codes">
              <label>Codes (comma-separated)</label>
              <input type="text" v-model="cat.codesStr" placeholder="e.g., 101, 102, MUS" class="input-codes" @input="updateCodes(cat)">
            </div>
          </div>
          <button class="btn-remove" @click="removeCategory(index)" title="Remove category">
            <AppIcon name="trash" />
          </button>
        </div>
      </div>

      <button class="btn-add-category" @click="addCategory">
        <AppIcon name="plus" />Add Category
      </button>

      <div class="preview-section">
        <h3>Preview</h3>
        <div class="preview-timeline">
          <div v-for="cat in categories" :key="cat.id" class="preview-block" :style="{ backgroundColor: cat.color }">
            <span class="preview-label">{{ cat.label }}</span>
          </div>
          <div class="preview-block preview-unknown" :style="{ backgroundColor: defaultTypeColor }">
            <span class="preview-label">UNKNOWN</span>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="loading-state">
      <p>Loading station...</p>
    </div>

    <!-- Discard Changes Modal -->
    <ConfirmModal
      :visible="showDiscardModal"
      type="warning"
      title="Unsaved Changes"
      message="You have unsaved changes. Are you sure you want to discard them?"
      confirmText="Discard"
      cancelText="Keep Editing"
      @confirm="confirmDiscard"
      @cancel="showDiscardModal = false"
    />

    <!-- Delete Category Modal -->
    <ConfirmModal
      :visible="showDeleteModal"
      type="danger"
      title="Delete Category"
      :message="`Are you sure you want to delete the '${categoryToDelete ? categoryToDelete.label : ''}' category?`"
      confirmText="Delete"
      cancelText="Cancel"
      @confirm="confirmDeleteCategory"
      @cancel="showDeleteModal = false"
    />
    
    <!-- Error Modal -->
    <ConfirmModal
      :visible="showErrorModal"
      title="Error"
      :message="errorMessage"
      type="danger"
      confirmText="OK"
      :showCancel="false"
      @confirm="showErrorModal = false"
    />
    
    <!-- Copy from Station Modal -->
    <div v-if="showCopyModal" class="modal-overlay">
      <div class="modal copy-modal">
        <h2>Copy Categories from Station</h2>
        <p class="modal-description">Select a station to copy its category configuration. This will replace your current categories.</p>
        
        <div class="station-list">
          <div 
            v-for="s in otherStations" 
            :key="s.slug" 
            class="station-option"
            :class="{ selected: selectedSourceStation === s.slug }"
            @click="selectedSourceStation = s.slug"
          >
            <div class="station-option-radio">
              <AppIcon :name="selectedSourceStation === s.slug ? 'check-circle' : 'circle'" />
            </div>
            <div class="station-option-info">
              <span class="station-option-name">{{ s.name }}</span>
              <span class="station-option-categories">{{ s.categoryCount }} categories</span>
            </div>
          </div>
        </div>
        
        <div v-if="otherStations.length === 0" class="no-stations-message">
          No other stations available to copy from.
        </div>
        
        <div class="modal-actions">
          <button class="btn-secondary" @click="showCopyModal = false">Cancel</button>
          <button 
            class="btn-primary" 
            @click="copyFromStation" 
            :disabled="!selectedSourceStation || loadingCopy"
          >
            <AppIcon v-if="loadingCopy" name="spinner" spin />
            {{ loadingCopy ? 'Copying...' : 'Copy Categories' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ConfirmModal from '../components/ConfirmModal.vue'
import BreadcrumbBar from '../components/BreadcrumbBar.vue'

export default {
  name: 'TypeCategoriesView',
  components: { ConfirmModal, BreadcrumbBar },
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      station: null,
      categories: [],
      defaultTypeColor: '#999999',
      originalCategories: null,
      originalDefaultColor: null,
      saving: false,
      showDiscardModal: false,
      showDeleteModal: false,
      categoryToDelete: null,
      showErrorModal: false,
      errorMessage: '',
      // Copy from station
      showCopyModal: false,
      allStations: [],
      selectedSourceStation: null,
      loadingCopy: false
    }
  },
  computed: {
    hasChanges() {
      if (!this.originalCategories) return false
      if (this.defaultTypeColor !== this.originalDefaultColor) return true
      return JSON.stringify(this.categories) !== JSON.stringify(this.originalCategories)
    },
    otherStations() {
      return this.allStations.filter(s => s.slug !== this.slug)
    }
  },
  async created() {
    await Promise.all([
      this.loadStation(),
      this.loadAllStations()
    ])
  },
  methods: {
    async loadStation() {
      try {
        const { fetchStationBySlug } = await import('../services/api')
        this.station = await fetchStationBySlug(this.slug)
        
        if (this.station) {
          // Initialize categories with codesStr for editing
          const cats = this.station.typeCategories || []
          this.categories = cats.map(c => ({
            ...c,
            codesStr: c.codes.join(', ')
          }))
          this.defaultTypeColor = this.station.defaultTypeColor || '#999999'
          
          // Store originals for change detection
          this.originalCategories = JSON.parse(JSON.stringify(this.categories))
          this.originalDefaultColor = this.defaultTypeColor
        }
      } catch (err) {
        console.error('Error loading station:', err)
      }
    },
    
    async loadAllStations() {
      try {
        const { fetchStations } = await import('../services/api')
        const data = await fetchStations()
        // Map stations with category count
        this.allStations = data.stations.map(s => ({
          slug: s.slug,
          name: s.name,
          categoryCount: s.typeCategoryCount || 0
        }))
      } catch (err) {
        console.error('Error loading stations:', err)
      }
    },
    
    async copyFromStation() {
      if (!this.selectedSourceStation) return
      
      this.loadingCopy = true
      try {
        const { fetchStationBySlug } = await import('../services/api')
        const sourceStation = await fetchStationBySlug(this.selectedSourceStation)
        
        if (sourceStation) {
          // Copy categories
          const cats = sourceStation.typeCategories || []
          this.categories = cats.map(c => ({
            ...c,
            id: 'cat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
            codesStr: c.codes.join(', ')
          }))
          
          // Copy default color
          this.defaultTypeColor = sourceStation.defaultTypeColor || '#999999'
          
          this.showCopyModal = false
          this.selectedSourceStation = null
        }
      } catch (err) {
        console.error('Error copying categories:', err)
        this.errorMessage = 'Failed to copy categories: ' + err.message
        this.showErrorModal = true
      } finally {
        this.loadingCopy = false
      }
    },
    
    updateCodes(cat) {
      // Parse comma-separated codes into array
      cat.codes = cat.codesStr
        .split(',')
        .map(c => c.trim().toUpperCase())
        .filter(c => c.length > 0)
    },
    
    addCategory() {
      const id = 'cat_' + Date.now()
      this.categories.push({
        id,
        label: '',
        color: '#888888',
        codes: [],
        codesStr: ''
      })
    },
    
    removeCategory(index) {
      this.categoryToDelete = { ...this.categories[index], index }
      this.showDeleteModal = true
    },
    
    confirmDeleteCategory() {
      if (this.categoryToDelete !== null) {
        this.categories.splice(this.categoryToDelete.index, 1)
      }
      this.showDeleteModal = false
      this.categoryToDelete = null
    },
    
    async saveCategories() {
      this.saving = true
      
      try {
        // Prepare categories for save (remove codesStr, ensure codes array)
        const categoriesToSave = this.categories.map(c => ({
          id: c.id,
          label: c.label,
          color: c.color,
          codes: c.codesStr
            .split(',')
            .map(code => code.trim().toUpperCase())
            .filter(code => code.length > 0)
        })).filter(c => c.label && c.codes.length > 0) // Only save valid categories
        
        const { updateStation } = await import('../services/api')
        await updateStation(this.slug, {
          typeCategories: categoriesToSave,
          defaultTypeColor: this.defaultTypeColor
        })
        
        // Update originals after successful save
        this.originalCategories = JSON.parse(JSON.stringify(this.categories))
        this.originalDefaultColor = this.defaultTypeColor
        
        // Navigate back after successful save
        this.$router.push('/')
      } catch (err) {
        console.error('Error saving categories:', err)
        this.errorMessage = 'Failed to save categories: ' + err.message
        this.showErrorModal = true
      } finally {
        this.saving = false
      }
    },
    
    goBack() {
      if (this.hasChanges) {
        this.showDiscardModal = true
      } else {
        this.$router.back()
      }
    },
    
    confirmDiscard() {
      this.showDiscardModal = false
      this.$router.back()
    }
  }
}
</script>

<style scoped>
.type-categories-view {
  min-height: calc(100vh - 74px);
  background: var(--gray-50);
}

.content {
  padding: 32px;
  max-width: 900px;
  margin: 0 auto;
}

.content-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-secondary {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-secondary:hover {
  background: var(--gray-50);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--accent);
  border-radius: 4px;
  background: var(--accent);
  color: var(--white);
  cursor: pointer;
}

.btn-primary:hover {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.info-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  color: #0369a1;
  font-size: 13px;
  margin-bottom: 24px;
}

.info-banner svg {
  font-size: 16px;
}

.default-color-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  margin-bottom: 24px;
}

.default-color-row label {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-700);
}

.color-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-picker {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  cursor: pointer;
  background: none;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 2px;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 2px;
}

.color-text {
  width: 80px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: monospace;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  text-transform: uppercase;
}

.categories-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.category-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
}

.category-color {
  flex-shrink: 0;
  padding-top: 24px;
}

.category-fields {
  flex: 1;
  display: flex;
  gap: 12px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-group label {
  font-size: 11px;
  font-weight: 500;
  color: var(--gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.input-label {
  width: 140px;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
}

.field-codes {
  flex: 1;
}

.input-codes {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  font-family: monospace;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
}

.input-label:focus,
.input-codes:focus,
.color-text:focus {
  outline: none;
  border-color: var(--gray-400);
}

.btn-remove {
  width: 36px;
  height: 36px;
  margin-top: 24px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  background: var(--white);
  color: var(--gray-400);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-remove:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #ef4444;
}

.btn-add-category {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  border: 2px dashed var(--gray-200);
  border-radius: 6px;
  background: none;
  color: var(--gray-500);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-add-category:hover {
  border-color: var(--gray-300);
  background: var(--gray-50);
  color: var(--gray-700);
}

.preview-section {
  margin-top: 32px;
  padding: 20px;
  background: var(--white);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
}

.preview-section h3 {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-600);
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-timeline {
  display: flex;
  height: 50px;
  border-radius: 4px;
  overflow: hidden;
}

.preview-block {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
}

.preview-label {
  font-size: 10px;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  text-transform: uppercase;
}

.preview-unknown {
  flex: 0.5;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  color: var(--gray-500);
}

/* Copy Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.copy-modal {
  background: var(--white);
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 440px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.copy-modal h2 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-900);
}

.modal-description {
  margin: 0 0 20px;
  font-size: 14px;
  color: var(--gray-600);
  line-height: 1.5;
}

.station-list {
  max-height: 300px;
  overflow-y: auto;
  margin: 0 -24px;
  padding: 0 24px;
}

.station-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 8px;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.station-option:hover {
  border-color: var(--gray-300);
  background: var(--gray-50);
}

.station-option.selected {
  border-color: var(--gray-900);
  background: var(--gray-50);
}

.station-option-radio {
  font-size: 18px;
  color: var(--gray-300);
}

.station-option.selected .station-option-radio {
  color: var(--gray-900);
}

.station-option-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.station-option-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--gray-900);
}

.station-option-categories {
  font-size: 12px;
  color: var(--gray-500);
}

.no-stations-message {
  padding: 20px;
  text-align: center;
  color: var(--gray-500);
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--gray-100);
}
</style>
